import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../config/api';

interface part {
  titre: string;
  numberOfQuestions: number;
  numberOfAnswers: number;
}

interface Question {
  questionNumber: number;
  numberOfAnswers: number;
  partIndex: number;
}

interface LocationState {
  testTitle: string;
  testType?: 'simple' | 'detailed';
  parts: part[];
  templateId?: number | null;
}

interface TemplateQuestion {
  id: number;
  template_id: number;
  question_number: number;
  number_of_answers: number;
  part_id: number | null;
}

interface TemplatePart {
  id: number;
  template_id: number;
  titre: string;
  ordre: number;
}

const ConfigureTest: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const [questions, setQuestions] = useState<Question[]>([]);
  const [testTitle, setTestTitle] = useState<string>('');
  const [testType, setTestType] = useState<'simple' | 'detailed'>('simple');
  const [parts, setparts] = useState<part[]>([]);
  const [expandedparts, setExpandedparts] = useState<{ [key: number]: boolean }>({});

  useEffect(() => {
    // Si pas de données, rediriger vers create-test
    if (!state || !state.testTitle || !state.parts || state.parts.length === 0) {
      navigate('/create-test');
      return;
    }

    // Initialiser les questions avec le nombre de réponses par défaut selon les parts
    setTestTitle(state.testTitle);
    setTestType(state.testType || 'simple');
    setparts(state.parts);

    // Initialiser toutes les parts comme ouvertes par défaut
    const initialExpanded: { [key: number]: boolean } = {};
    state.parts.forEach((_, index) => {
      initialExpanded[index] = true;
    });
    setExpandedparts(initialExpanded);

    // Si un templateId est fourni, charger la configuration depuis la BDD
    if (state.templateId) {
      loadTemplateConfiguration(state.templateId);
    } else {
      // Sinon, générer les questions avec les valeurs par défaut
      const initialQuestions: Question[] = [];
      let questionNumber = 1;

      state.parts.forEach((part, partIndex) => {
        for (let i = 0; i < part.numberOfQuestions; i++) {
          initialQuestions.push({
            questionNumber: questionNumber++,
            numberOfAnswers: part.numberOfAnswers,
            partIndex: partIndex
          });
        }
      });

      setQuestions(initialQuestions);
    }
  }, [state, navigate]);

  const loadTemplateConfiguration = async (templateId: number): Promise<void> => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(t('tests.configure.errors.loadTemplate'));
      }

      const data = await response.json();
      const templateParts: TemplatePart[] = data.parts;
      const templateQuestions: TemplateQuestion[] = data.questions;

      // Créer un mapping des part_id vers partIndex
      const partIdToIndexMap = new Map<number, number>();
      templateParts.forEach((part, index) => {
        partIdToIndexMap.set(part.id, index);
      });

      // Construire les questions avec la configuration du template
      const loadedQuestions: Question[] = templateQuestions.map(tq => ({
        questionNumber: tq.question_number,
        numberOfAnswers: tq.number_of_answers,
        partIndex: tq.part_id ? (partIdToIndexMap.get(tq.part_id) ?? 0) : 0
      }));

      setQuestions(loadedQuestions);
    } catch (err) {
      console.error('Erreur:', err);
      // En cas d'erreur, utiliser la configuration par défaut
      const initialQuestions: Question[] = [];
      let questionNumber = 1;

      state.parts.forEach((part, partIndex) => {
        for (let i = 0; i < part.numberOfQuestions; i++) {
          initialQuestions.push({
            questionNumber: questionNumber++,
            numberOfAnswers: part.numberOfAnswers,
            partIndex: partIndex
          });
        }
      });

      setQuestions(initialQuestions);
    }
  };

  const handleBack = (): void => {
    navigate('/create-test');
  };

  const togglepart = (partIndex: number): void => {
    setExpandedparts(prev => ({
      ...prev,
      [partIndex]: !prev[partIndex]
    }));
  };

  const handleAnswersChange = (questionNumber: number, value: string): void => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 2 && numValue <= 10) {
      setQuestions(prev =>
        prev.map(q =>
          q.questionNumber === questionNumber
            ? { ...q, numberOfAnswers: numValue }
            : q
        )
      );
    }
  };

  const handleValidate = async (): Promise<void> => {
    console.log('=== DÉBUT DE LA VALIDATION ===');

    const token = localStorage.getItem('authToken');
    console.log('Token:', token ? 'Présent' : 'Absent');

    if (!token) {
      navigate('/login');
      return;
    }

    // Si test détaillé, naviguer vers la page de configuration détaillée
    if (testType === 'detailed') {
      navigate('/detailed-test-configuration', {
        state: {
          testTitle,
          testType,
          questions,
          parts
        }
      });
      return;
    }

    // Sinon, continuer avec le flux normal pour les tests simples
    // Calculer le nombre de questions par défaut (le plus courant)
    const answerCounts: { [key: number]: number } = {};
    questions.forEach(q => {
      answerCounts[q.numberOfAnswers] = (answerCounts[q.numberOfAnswers] || 0) + 1;
    });
    const defaultAnswers = parseInt(Object.keys(answerCounts).reduce((a, b) =>
      answerCounts[parseInt(a)] > answerCounts[parseInt(b)] ? a : b
    ));

    const payload = {
      testTitle: testTitle,
      type: 'simple',
      numberOfQuestions: questions.length,
      numberOfAnswers: defaultAnswers,
      questions: questions,
      parts: parts
    };

    console.log('=== PAYLOAD DÉTAILLÉ ===');
    console.log('Payload complet:', JSON.stringify(payload, null, 2));
    console.log('Nombre total de questions:', questions.length);
    console.log('Nombre de parts:', parts.length);
    console.log('Exemples de questions avec parts:');
    questions.slice(0, 5).forEach(q => {
      console.log(`  Q${q.questionNumber}: ${q.numberOfAnswers} réponses, part ${q.partIndex}`);
    });

    try {
      console.log(`Envoi de la requête à ${API_BASE_URL}/api/tests/create`);

      const response = await fetch(`${API_BASE_URL}/api/tests/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      console.log('Réponse reçue, status:', response.status);
      console.log('Response OK?:', response.ok);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Erreur du serveur:', errorData);
        throw new Error(errorData.error || t('tests.configure.errors.createTest'));
      }

      const data = await response.json();
      console.log('Test créé avec succès:', data);

      // Rediriger vers mes tests
      navigate('/my-tests');

    } catch (error) {
      console.error('=== ERREUR COMPLÈTE ===');
      console.error('Type:', error);
      console.error('Message:', error instanceof Error ? error.message : 'Erreur inconnue');
      console.error('Stack:', error instanceof Error ? error.stack : 'N/A');
    }
  };

  if (!state || questions.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="text-gray-600 hover:text-gray-800 transition"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">{t('tests.configure.title')} : {testTitle}</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {t('tests.configure.pageTitle')}
            </h2>
            <p className="text-gray-600">
              {t('tests.configure.description')}
            </p>
          </div>

          {/* Tableau des questions organisé par parts */}
          <div className="space-y-6">
            {parts.map((part, partIndex) => {
              const partQuestions = questions.filter(q => q.partIndex === partIndex);

              const isExpanded = expandedparts[partIndex];

              return (
                <div key={partIndex} className="border border-gray-300 rounded-lg overflow-hidden">
                  {/* En-tête de la part */}
                  <button
                    type="button"
                    onClick={() => togglepart(partIndex)}
                    className="w-full bg-blue-600 px-4 py-3 hover:bg-blue-700 transition flex items-center justify-between"
                  >
                    <div className="text-left">
                      <h3 className="text-white font-bold text-lg">
                        {t('tests.configure.part')} {partIndex + 1}: {part.titre}
                      </h3>
                      <p className="text-blue-100 text-sm">
                        {part.numberOfQuestions} {t('tests.configure.questionsCount')} • {part.numberOfAnswers} {t('tests.configure.defaultAnswers')}
                      </p>
                    </div>
                    <div className="text-white">
                      {isExpanded ? (
                        <ChevronUp className="w-6 h-6" />
                      ) : (
                        <ChevronDown className="w-6 h-6" />
                      )}
                    </div>
                  </button>

                  {/* Tableau des questions de cette part */}
                  {isExpanded && (
                    <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-800">
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('tests.configure.questionNumber')}
                          </th>
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('tests.configure.answers')}
                          </th>
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('tests.configure.questionNumber')}
                          </th>
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('tests.configure.answers')}
                          </th>
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('tests.configure.questionNumber')}
                          </th>
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('tests.configure.answers')}
                          </th>
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('tests.configure.questionNumber')}
                          </th>
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('tests.configure.answers')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Diviser les questions en 4 groupes égaux
                          const quarterSize = Math.ceil(partQuestions.length / 4);
                          const group1 = partQuestions.slice(0, quarterSize);
                          const group2 = partQuestions.slice(quarterSize, quarterSize * 2);
                          const group3 = partQuestions.slice(quarterSize * 2, quarterSize * 3);
                          const group4 = partQuestions.slice(quarterSize * 3);

                          // Nombre de lignes = taille du plus grand groupe
                          const maxRows = Math.max(group1.length, group2.length, group3.length, group4.length);

                          return Array.from({ length: maxRows }).map((_, rowIndex) => {
                            const q1 = group1[rowIndex];
                            const q2 = group2[rowIndex];
                            const q3 = group3[rowIndex];
                            const q4 = group4[rowIndex];

                            const renderQuestion = (q: typeof q1) => {
                              if (!q) {
                                return (
                                  <>
                                    <td className="border border-gray-300 px-2 py-2"></td>
                                    <td className="border border-gray-300 px-2 py-2"></td>
                                  </>
                                );
                              }

                              return (
                                <>
                                  <td className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-semibold text-sm">
                                    Q{q.questionNumber}
                                  </td>
                                  <td className="border border-gray-300 px-2 py-2 text-center">
                                    <input
                                      type="number"
                                      value={q.numberOfAnswers}
                                      onChange={(e) => handleAnswersChange(q.questionNumber, e.target.value)}
                                      min="2"
                                      max="10"
                                      className="w-14 px-1 py-1 border border-gray-300 rounded text-center text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </td>
                                </>
                              );
                            };

                            return (
                              <tr
                                key={rowIndex}
                                className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-100'} hover:bg-blue-50`}
                              >
                                {renderQuestion(q1)}
                                {renderQuestion(q2)}
                                {renderQuestion(q3)}
                                {renderQuestion(q4)}
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bouton Valider */}
          <div className="flex justify-end mt-8">
            <button
              onClick={handleValidate}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-8 rounded-lg transition"
            >
              <Check className="w-5 h-5" />
              {t('tests.configure.validateConfiguration')}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ConfigureTest;
